import { AxiosResponse } from "axios";
import { FC, RefObject, useCallback, useRef, useState } from "react";
import { createContext } from "use-context-selector";
import { getMessages } from "../api/rooms/getMessages";
import { IMessage } from "../common/interfaces/IMessage";
import { useInfo } from "../hooks/useInfo";
import { useRemoteParticipant } from "../hooks/useRemoteParticipant";
import { useRoom } from "../hooks/useRoom";
import { useVideoChat } from "../hooks/useVideoChat";
import { showToast } from "../utils/showToast";

export type ChatContextType = {
  open: boolean;
  handleChatOpen: (status: boolean) => void;
  messages: IMessage[];
  loadMessages: (isNewMessage: boolean) => void;
  concatMessages: (newMessage: IMessage[]) => void;
  files: File[];
  addFiles: (newFiles: File[] | FileList) => void;
  removeFile: (removedIndex: number) => void;
  clearFiles: () => void;
  fileInputRef: RefObject<HTMLInputElement>;
  notification: boolean;
  handleSetNotification: (value: boolean) => void;
};

export const ChatContext = createContext({} as ChatContextType);

// eslint-disable-next-line @typescript-eslint/no-var-requires
const sound = require("../assets/sounds/new_message.mp3");

const audio = new Audio(sound);

export const ChatContextProvider = ({ children }: { children: any }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [notification, setNotification] = useState<boolean>(false);

  const { telemedicineData } = useInfo();
  const { permission } = useVideoChat();
  const { room } = useRoom();
  const { remoteParticipant } = useRemoteParticipant();

  const handleChatOpen = useCallback((status: boolean) => {
    setNotification(false);
    setOpen(status);
  }, []);

  /**
   * Busca pelas mensagens enviadas no chat
   */
  const loadMessages = useCallback(
    (isNewMessage: boolean) => {
      getMessages({
        lastId: null
      }).then((response: AxiosResponse<IMessage[]>) => {
        setMessages(response.data);
        if (isNewMessage && !open) {
          setNotification(true);
          audio.play();
        }
      });
    },
    [telemedicineData, permission, room, remoteParticipant]
  );

  const concatMessages = useCallback((newMessages: IMessage[]) => {
    setMessages((prevMessages) => [...prevMessages, ...newMessages]);
  }, []);

  const addFiles = useCallback(
    (newFiles: File[] | FileList) => {
      const newFilesArray = Array.from(newFiles);

      // Filtra os tipos dos arquivos que são aceitos pelo chat
      const filteredFiles = Array.from(newFilesArray).filter((file) =>
        [
          "image/jpeg",
          "image/png",
          "image/jpg",
          "image/tif",
          "image/bmp",
          "application/pdf",
        ].includes(file.type)
      );

      // Verifica se esta tentando anexar mais arquivos que o possível
      if (filteredFiles.length > 3 - files.length || files.length === 3) {
        showToast("info", {
          text: "É permitido enviar apenas 3 arquivos de imagem e pdfs.",
          timer: 3000,
        });
        setFiles((prevFiles) => [
          ...prevFiles,
          ...filteredFiles.slice(0, 3 - files.length),
        ]);
        return;
      }

      if (filteredFiles.length < newFilesArray.length) {
        showToast("info", {
          text: "São aceitos apenas arquivos de imagem e pdfs.",
          timer: 3000,
        });
      }

      setFiles((prevFiles) => [...prevFiles, ...filteredFiles]);
    },
    [files]
  );

  const removeFile = useCallback((removedIndex: number) => {
    setFiles((prevFiles) =>
      prevFiles.filter((item, index) => index !== removedIndex)
    );
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  const handleSetNotification = useCallback((value: boolean) => {
    setNotification(value);
  }, []);

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <ChatContext.Provider
      value={{
        open,
        handleChatOpen,
        messages,
        loadMessages,
        concatMessages,
        files,
        addFiles,
        removeFile,
        clearFiles,
        fileInputRef,
        notification,
        handleSetNotification,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
